// Vertical Type Tool -- creates text layers with vertical orientation.
// Photoshop: T -> Vertical Type Tool.

import { screenToDoc } from "../render/Viewport";
import { createTypeLayer, isTypeLayer, type TypeLayer } from "../document/TypeLayer";
import { NewLayerCommand } from "../commands/NewLayerCommand";
import { SetLayerPropertyCommand } from "../commands/SetLayerPropertyCommand";
import type { Layer } from "../document/Layer";
import type { PointerSample, Tool, ToolContext } from "./Tool";
import { approximateMeasureText, type MeasureTextFn } from "./TypeTool";

export interface VerticalTypeToolCallbacks {
  requestNewText: (x: number, y: number) => Promise<string | null>;
  requestEditText: (layer: TypeLayer, opts?: { selectAll?: boolean }) => Promise<string | null>;
  commitActiveEditor?: () => void;
}

export class VerticalTypeTool implements Tool {
  readonly id = "type-vertical";
  readonly shortcut = "T";
  private editingId: string | null = null;
  private callbacks: VerticalTypeToolCallbacks | null = null;

  /** Set by main.ts to provide accurate Canvas2D text metrics. */
  measureText: MeasureTextFn | null = null;

  setCallbacks(callbacks: VerticalTypeToolCallbacks): void {
    this.callbacks = callbacks;
  }

  onDeactivate(): void {
    this.callbacks?.commitActiveEditor?.();
  }

  onPointerDown(ctx: ToolContext, s: PointerSample): void {
    if (!ctx.doc) return;
    const p = screenToDoc(ctx.viewport, s.x, s.y);

    const clickedLayer = this.findTypeLayerAt(ctx.doc.layers, p.x, p.y);
    if (clickedLayer) {
      ctx.emit(new SetLayerPropertyCommand(clickedLayer.id, { visible: true }));
      this.editTypeLayer(ctx, clickedLayer);
      return;
    }

    this.createNewTypeLayer(ctx, p.x, p.y);
  }

  private async createNewTypeLayer(ctx: ToolContext, x: number, y: number): Promise<void> {
    if (!this.callbacks) return;
    const text = await this.callbacks.requestNewText(x, y);
    if (!text || !ctx.doc) return;

    const layer = createTypeLayer({
      text,
      x: Math.round(x),
      y: Math.round(y),
      name: text.slice(0, 20) || "Vertical Type",
      vertical: true,
    });
    ctx.emit(new NewLayerCommand(layer));
  }

  private async editTypeLayer(ctx: ToolContext, layer: TypeLayer): Promise<void> {
    if (!this.callbacks) return;
    this.editingId = layer.id;
    const text = await this.callbacks.requestEditText(layer);
    if (text === null || text === layer.text) return;
    ctx.emit(new SetLayerPropertyCommand(layer.id, { text }));
  }

  private findTypeLayerAt(layers: readonly Layer[], x: number, y: number): TypeLayer | null {
    for (let i = layers.length - 1; i >= 0; i--) {
      const l = layers[i]!;
      if (isTypeLayer(l)) {
        const bounds = this.getTypeLayerBounds(l);
        if (bounds && x >= bounds.x && x <= bounds.x + bounds.w && y >= bounds.y && y <= bounds.y + bounds.h) {
          return l;
        }
      }
      if (l.kind === "group") {
        const found = this.findTypeLayerAt(l.children, x, y);
        if (found) return found;
      }
    }
    return null;
  }

  private getTypeLayerBounds(layer: TypeLayer): { x: number; y: number; w: number; h: number } | null {
    if (layer.text.length === 0) return null;

    let weight = layer.fontWeight;
    if (layer.fauxBold) weight = Math.min(900, weight + 300);
    let style = layer.fontStyle;
    if (layer.fauxItalic && style === "normal") style = "italic";
    const fontSize = layer.fontSize * (layer.superscript ? 0.6 : layer.subscript ? 0.6 : 1);
    const lines = layer.text.split("\n");
    const lineHeight = layer.fontSize * layer.leading;
    const padding = 4;

    if (layer.vertical) {
      // Vertical (CJK) layout: each line is one column advancing
      // top-to-bottom, and columns advance right-to-left. Mirrors
      // Canvas2DRenderer.getTypeLayerTextRect's vertical branch so this
      // tool's click hit-testing matches the actual rendered extent (#i15).
      // `lineHeight` (fontSize * leading) is reused as the column-to-column
      // advance (was the line-to-line advance in horizontal layout);
      // `fontSize` is the per-glyph advance within a column.
      const columnAdvance = lineHeight;
      const charAdvance = fontSize;
      const numColumns = lines.length;
      const maxChars = Math.max(1, ...lines.map((l) => Array.from(l).length));

      let y = layer.y;
      if (layer.textBaseline === "middle") y -= (maxChars * charAdvance) / 2;
      else if (layer.textBaseline === "bottom") y -= maxChars * charAdvance;

      const totalColumnsWidth = (numColumns - 1) * columnAdvance;
      let anchorX = layer.x;
      if (layer.textAlign === "center") anchorX += totalColumnsWidth / 2;
      else if (layer.textAlign !== "right") anchorX += totalColumnsWidth; // "left"/"justify"

      const blockWidth = numColumns * columnAdvance;
      const blockHeight = maxChars * charAdvance;
      const x = anchorX - totalColumnsWidth - columnAdvance / 2;

      return { x: x - padding, y: y - padding, w: blockWidth + padding * 2, h: blockHeight + padding * 2 };
    }

    const fontCss = `${style} ${weight} ${fontSize}px "${layer.fontFamily}"`;
    const measure = this.measureText ?? approximateMeasureText;

    let maxWidth = 0;
    for (const line of lines) {
      const w = measure(line, fontCss) + Math.abs(layer.tracking) * 0.01 * fontSize * line.length;
      if (w > maxWidth) maxWidth = w;
    }

    const totalHeight = lines.length * lineHeight;
    let x = layer.x;
    if (layer.textAlign === "center") x -= maxWidth / 2;
    else if (layer.textAlign === "right") x -= maxWidth;
    let y = layer.y;
    if (layer.textBaseline === "middle") y -= totalHeight / 2;
    else if (layer.textBaseline === "bottom") y -= totalHeight;

    return { x: x - padding, y: y - padding, w: maxWidth + padding * 2, h: totalHeight + padding * 2 };
  }
}
